import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'
import { useApplication } from 'stimulus-use'
import consumer from '../channels/consumer'

// Connects to data-controller="occupancy"
export default class extends Controller {
  static targets = ['input', 'occupier', 'cancel']
  static values = {
    attribute: String,
    gid: String
  }

  connect () {
    useApplication(this)
    if (this.isPreview) return

    // A morph refresh syncs class attributes back to their server-rendered
    // values without reconnecting targets, stripping the self marking.
    this.remarkOccupiers = () => this.occupierTargets.forEach((occupier) => this.markOccupier(occupier))
    document.addEventListener('turbo:morph', this.remarkOccupiers)

    this.subscription = consumer.subscriptions.create({
      channel: 'OccupancyChannel',
      gid: this.gidValue,
      attribute: this.attributeValue
    }, {
      received: (message) => this.received(message),
      // Fires on every (re)connect, so an editor re-asserts its occupancy
      // after any interruption.
      connected: () => { if (this.editing) this.occupy() },
      // A rejected subscription would otherwise resubscribe on every reconnect.
      rejected: () => {
        this.subscription?.unsubscribe()
        this.subscription = null
      }
    })
  }

  disconnect () {
    document.removeEventListener('turbo:morph', this.remarkOccupiers)
    this.subscription?.unsubscribe()
  }

  // Target callbacks can fire before connect() has created the subscription
  // (Stimulus starts target observers first); `editing` records the intent and
  // the subscription's connected callback asserts it once the channel is up.
  occupy (event) {
    this.editing = true
    this.element.closest('form')?.setAttribute('data-turbo-permanent', 'true')
    this.subscription?.perform('occupy')
  }

  vacate (event) {
    if (!this.editing) return

    this.stopEditing()
    this.subscription?.perform('vacate')
  }

  release (event) {
    this.subscription?.perform('release')
  }

  inputTargetConnected (event) {
    if (this.isPreview) return

    this.occupy()
  }

  inputTargetDisconnected (event) {
    if (this.isPreview) return

    this.vacate()
  }

  occupierTargetConnected (occupier) {
    this.markOccupier(occupier)
  }

  received (message) {
    if (typeof message === 'string') return Turbo.renderStreamMessage(message)
    if (message.event === 'vacated') this.standDown()
  }

  // The claim was deliberately released elsewhere, so an open editor here must
  // not keep, or re-assert, it. `editing` is cleared first: even without a
  // cancel control the claim must stay released. The server is told too —
  // clearing `editing` makes the Cancel path skip its vacate, and a channel
  // left believing it occupies the field would vacate a future claim on
  // unsubscribe. The acknowledgement only clears the channel's flag: a vacate
  // here could arrive after the field was reacquired and delete the new claim.
  standDown () {
    if (!this.editing) return

    this.stopEditing()
    this.subscription?.perform('acknowledge_release')
    if (this.hasCancelTarget) this.cancelTarget.click()
  }

  // Occupier avatars arrive viewer-agnostic (one broadcast serves everyone),
  // so mark the viewer's own entry and strip the release control from others.
  markOccupier (occupier) {
    const self = occupier.dataset.userId === this.currentUserId

    occupier.classList.toggle('occupier-self', self)
    if (!self) occupier.querySelector('button')?.remove()
  }

  stopEditing () {
    this.editing = false
    this.element.closest('form')?.removeAttribute('data-turbo-permanent')
  }

  get currentUserId () {
    return window.Current?.user?.id
  }
}
